import { useEffect } from 'react';

export default function About() {
  useEffect(() => {
    // SEO JSON-LD pour la page à propos
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "À propos de Lolotte - Les Carnets de Lolotte",
      "description": "Découvrez l'histoire de Nathalie, artisane passionnée derrière Les Carnets de Lolotte. Créations artisanales en papier plié, faites main avec amour.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "mainEntity": {
        "@type": "Person",
        "name": "Nathalie",
        "jobTitle": "Artisane créatrice",
        "description": "Passionnée de papier et de créations faites main, spécialisée dans l'art du pliage de livres",
        "worksFor": {
          "@type": "Organization",
          "name": "Les Carnets de Lolotte"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "À propos de Lolotte - Artisane passionnée | Les Carnets de Lolotte";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Découvrez l\'histoire de Nathalie, artisane passionnée derrière Les Carnets de Lolotte. Créations artisanales en papier plié, faites main avec amour et passion.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Hero Section */}
      <header className="py-20 bg-[#FAF9F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl font-bold text-[#B97A72] mb-8" style={{ fontFamily: 'Allura, cursive' }}>
            À propos de Lolotte
          </h1>
          <p className="text-2xl text-[#6B5E5A] leading-relaxed mb-6 font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
            Les Carnets de Lolotte proposent des créations artisanales en livres pliés, entre décoration poétique et cadeaux chargés de sens.
          </p>
          <p className="text-xl text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Découvrez l'histoire et la passion derrière chaque création artisanale
          </p>
        </div>
      </header>

      {/* Mon histoire */}
      <main>
        <section className="py-20 bg-[#F8EDEA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-center">
              <article>
                <h2 className="text-4xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                  Mon histoire
                </h2>
                <div className="space-y-6 text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p>
                    Je m'appelle Nathalie, passionnée de papier et de créations faites main.
                    Dans mon atelier, les livres se transforment en trésors, les pages deviennent poèmes.
                  </p>
                  <p>
                    Chaque pli, chaque ruban est une caresse, chaque création une histoire.
                    Bienvenue dans mon univers poétique – Les Carnets de Lolotte 🌸
                  </p>
                  <p>
                    Tout a commencé par une passion pour les livres et l'art du papier. 
                    J'ai découvert l'art du pliage de livres il y a quelques années, et depuis, 
                    je n'ai cessé d'explorer cette technique fascinante qui donne une seconde vie aux ouvrages.
                  </p>
                  <p>
                    Chaque création est unique, réalisée à partir de livres soigneusement sélectionnés. 
                    Je privilégie les ouvrages qui ont déjà vécu, qui portent en eux des histoires, 
                    pour leur offrir une nouvelle existence sous forme d'art décoratif.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Ma philosophie */}
        <section className="py-20 bg-[#EBD2CF]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Ma philosophie
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <article className="text-center">
                <div className="w-20 h-20 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Passion
                </h3>
                <p className="text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Chaque création naît d'une passion sincère pour l'art du papier et le travail manuel. 
                  Je mets tout mon cœur dans chaque pliage, chaque détail.
                </p>
              </article>
              
              <article className="text-center">
                <div className="w-20 h-20 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-leaf-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Éthique
                </h3>
                <p className="text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Je privilégie le recyclage et la seconde vie des objets. 
                  Mes créations sont éco-responsables et contribuent à réduire le gaspillage.
                </p>
              </article>
              
              <article className="text-center">
                <div className="w-20 h-20 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-magic-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Poésie
                </h3>
                <p className="text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Mes créations racontent des histoires, évoquent des émotions. 
                  Elles apportent une touche de poésie et de douceur dans votre quotidien.
                </p>
              </article>
            </div>
          </div>
        </section>

        <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-2xl p-6 sm:p-8 lg:p-10">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <i className="ri-heart-line text-3xl sm:text-5xl text-[#B97A72]"></i>
          </div>
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#B97A72] mb-3 sm:mb-4 text-center" style={{ fontFamily: 'Allura, cursive' }}>
            Ma passion
          </h3>
          <p className="text-sm sm:text-base lg:text-lg text-[#6B5E5A] leading-relaxed text-center mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Chaque création naît d'une émotion, d'un souvenir ou d'un rêve. Je puise mon inspiration dans la beauté des saisons, la magie de Noël et l'amour que je porte à cet art ancestral.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-[#6B5E5A] leading-relaxed text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Mon atelier, situé au cœur de ma maison, est un véritable cocon de créativité où chaque livre trouve une seconde vie sous mes doigts patients.
          </p>
        </div>

        {/* Mes engagements */}
        <section className="py-20 bg-[#B97A72] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Mes engagements
              </h2>
              <p className="text-xl opacity-90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Des valeurs qui guident chacune de mes créations
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <article className="bg-white/10 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <i className="ri-recycle-line text-3xl mr-4"></i>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Upcycling créatif
                  </h3>
                </div>
                <p className="opacity-90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Je donne une seconde vie aux livres en fin de parcours, 
                  transformant ce qui pourrait être jeté en œuvres d'art uniques.
                </p>
              </article>
              
              <article className="bg-white/10 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <i className="ri-hand-heart-line text-3xl mr-4"></i>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Fait main avec amour
                  </h3>
                </div>
                <p className="opacity-90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Chaque pièce est entièrement réalisée à la main, 
                  avec patience et attention aux moindres détails.
                </p>
              </article>
              
              <article className="bg-white/10 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <i className="ri-star-line text-3xl mr-4"></i>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Pièces uniques
                  </h3>
                </div>
                <p className="opacity-90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Aucune création n'est identique à une autre. 
                  Chaque livre apporte sa propre histoire et ses particularités.
                </p>
              </article>
              
              <article className="bg-white/10 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <i className="ri-customer-service-line text-3xl mr-4"></i>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Service personnalisé
                  </h3>
                </div>
                <p className="opacity-90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Je suis à votre écoute pour créer des pièces sur mesure 
                  qui correspondent parfaitement à vos envies et votre intérieur.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
